import { io, Socket } from 'socket.io-client';
import { AccessLog, RecentEvent } from '../types';

type EventHandler = (data: any) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private handlers: Map<string, EventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;

  // token es opcional — si está presente se envía como auth.token (backward compat)
  // El browser también envía la cookie httpOnly access_token automáticamente
  connect(token?: string): void {
    if (this.socket?.connected) {
      console.log('WebSocket already connected');
      return;
    }

    // En producción VITE_API_URL está vacío → conectar al mismo origen de la página
    // (nginx proxying /socket.io/ → backend:3001)
    const baseUrl = import.meta.env.VITE_API_URL || window.location.origin;

    this.socket = io(baseUrl + '/dashboard', {
      ...(token ? { auth: { token } } : {}),
      withCredentials: true, // envía cookie access_token automáticamente
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.emit('status', { connected: true });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.emit('status', { connected: false, reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        this.emit('error', { message: 'Unable to connect to server' });
      }
    });

    // Access event handlers
    this.socket.on('access:granted', (data: AccessLog) => {
      this.emit('access:granted', data);
    });

    this.socket.on('access:denied', (data: AccessLog) => {
      this.emit('access:denied', data);
    });

    this.socket.on('access:forced', (data: AccessLog) => {
      this.emit('access:forced', data);
    });

    this.socket.on('access:event', (data: AccessLog) => {
      this.emit('access:event', data);
    });

    // Hub status events (real-time online/offline)
    this.socket.on('hub:status', (data: any) => {
      this.emit('hub:status', data);
    });

    // Device FSM state change (CRITICAL, DEGRADED, OK, etc.)
    this.socket.on('device:state_change', (data: any) => {
      this.emit('device:state_change', data);
    });

    // Device status events (real-time online/offline reported by Hub)
    this.socket.on('device:status', (data: any) => {
      this.emit('device:status', data);
    });

    // Hub telemetry events (hardware metrics from Hub)
    this.socket.on('hub:telemetry_update', (data: any) => {
      this.emit('hub:telemetry_update', data);
    });

    // Device config update events (relay mode changes)
    this.socket.on('device:config_update', (data: any) => {
      this.emit('device:config_update', data);
    });

    // Hub command update events (reboot, restart, etc.)
    this.socket.on('hub:command_update', (data: any) => {
      this.emit('hub:command_update', data);
    });

    // Controller events
    this.socket.on('controller:online', (data: any) => {
      this.emit('controller:online', data);
    });

    this.socket.on('controller:offline', (data: any) => {
      this.emit('controller:offline', data);
    });

    this.socket.on('controller:sync', (data: any) => {
      this.emit('controller:sync', data);
    });

    // User events
    this.socket.on('user:created', (data: any) => {
      this.emit('user:created', data);
    });

    this.socket.on('user:updated', (data: any) => {
      this.emit('user:updated', data);
    });

    this.socket.on('user:deleted', (data: any) => {
      this.emit('user:deleted', data);
    });

    // Permission events
    this.socket.on('permission:granted', (data: any) => {
      this.emit('permission:granted', data);
    });

    this.socket.on('permission:revoked', (data: any) => {
      this.emit('permission:revoked', data);
    });

    // Notification events
    this.socket.on('notification', (data: any) => {
      this.emit('notification', data);
    });

    // Alert events
    this.socket.on('alert', (data: any) => {
      this.emit('alert', data);
    });

    // Critical alerts (e.g. hub_extraction_alert)
    this.socket.on('alert:critical', (data: any) => {
      this.emit('alert:critical', data);
    });

    // Emergency status broadcast (activa/desactiva para todos los dashboards de la org)
    this.socket.on('emergency:status', (data: any) => {
      this.emit('emergency:status', data);
    });

    // Actualización de citas de visitantes (cualquier cambio de estado)
    this.socket.on('visitor:appointment_updated', (data: any) => {
      this.emit('visitor:appointment_updated', data);
    });

    // Actualización de reservas de espacios (creación, aprobación, cancelación, etc.)
    this.socket.on('booking:updated', (data: any) => {
      this.emit('booking:updated', data);
    });

    // Dashboard KPI delta (batch upload de logs desde el Hub)
    this.socket.on('dashboard:stats_update', (data: any) => {
      this.emit('dashboard:stats_update', data);
    });

    // Señal de refresco de lista de eventos (batch upload de logs desde el Hub)
    this.socket.on('dashboard:refresh_events', (data: any) => {
      this.emit('dashboard:refresh_events', data);
    });

    // ControlNat Enrollador (lector USB de huella de escritorio)
    this.socket.on('enroller:paired', (data: any) => {
      this.emit('enroller:paired', data);
    });
    this.socket.on('enroller:status', (data: any) => {
      this.emit('enroller:status', data);
    });
    this.socket.on('enroller:capture_progress', (data: any) => {
      this.emit('enroller:capture_progress', data);
    });
    this.socket.on('enroller:capture_result', (data: any) => {
      this.emit('enroller:capture_result', data);
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.handlers.clear();
      console.log('WebSocket disconnected');
    }
  }

  on(event: string, handler: EventHandler): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
    }
    this.handlers.get(event)!.push(handler);
  }

  off(event: string, handler?: EventHandler): void {
    if (!handler) {
      this.handlers.delete(event);
      return;
    }

    const handlers = this.handlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data: any): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }

  // Send events to server
  send(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('WebSocket not connected, cannot send event:', event);
    }
  }

  // Join a room (for organization-specific events)
  joinOrganization(organizationId: number): void {
    this.send('join:organization', { organizationId });
  }

  // Leave a room
  leaveOrganization(organizationId: number): void {
    this.send('leave:organization', { organizationId });
  }

  // Check connection status
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // Subscribe to real-time access logs (AccessLog format, legacy)
  subscribeToAccessLogs(callback: (log: AccessLog) => void): () => void {
    this.on('access:event', callback);
    return () => this.off('access:event', callback);
  }

  // Subscribe to real-time access events in RecentEvent format (push desde validate/online)
  subscribeToAccessEvents(callback: (event: RecentEvent) => void): () => void {
    this.on('access:event', callback);
    return () => this.off('access:event', callback);
  }

  // Subscribe to hub status changes (online/offline in real-time)
  subscribeToHubStatus(callback: (data: any) => void): () => void {
    this.on('hub:status', callback);
    return () => this.off('hub:status', callback);
  }

  // Subscribe to device status changes (online/offline reported by Hub)
  subscribeToDeviceStatus(callback: (data: any) => void): () => void {
    this.on('device:status', callback);
    return () => this.off('device:status', callback);
  }

  // Subscribe to controller status changes
  subscribeToControllerStatus(callback: (data: any) => void): () => void {
    this.on('controller:online', callback);
    this.on('controller:offline', callback);
    return () => {
      this.off('controller:online', callback);
      this.off('controller:offline', callback);
    };
  }

  // Subscribe to notifications
  subscribeToNotifications(callback: (notification: any) => void): () => void {
    this.on('notification', callback);
    return () => this.off('notification', callback);
  }

  // Subscribe to alerts
  subscribeToAlerts(callback: (alert: any) => void): () => void {
    this.on('alert', callback);
    return () => this.off('alert', callback);
  }

  // Subscribe to Hub telemetry updates (hardware metrics)
  subscribeToHubTelemetry(callback: (data: any) => void): () => void {
    this.on('hub:telemetry_update', callback);
    return () => this.off('hub:telemetry_update', callback);
  }

  // Subscribe to device FSM state changes (CRITICAL, OK, DEGRADED, etc.)
  subscribeToDeviceStateChange(callback: (data: any) => void): () => void {
    this.on('device:state_change', callback);
    return () => this.off('device:state_change', callback);
  }

  // Subscribe to device config updates (relay mode changes)
  subscribeToDeviceConfigUpdate(callback: (data: any) => void): () => void {
    this.on('device:config_update', callback);
    return () => this.off('device:config_update', callback);
  }

  // Subscribe to hub command updates (reboot, restart acks)
  subscribeToHubCommandUpdate(callback: (data: any) => void): () => void {
    this.on('hub:command_update', callback);
    return () => this.off('hub:command_update', callback);
  }

  // Subscribe to dashboard KPI deltas (batch upload de logs desde el Hub)
  subscribeToDashboardStatsUpdate(callback: (data: { granted_delta: number; denied_delta: number; total_delta: number }) => void): () => void {
    this.on('dashboard:stats_update', callback);
    return () => this.off('dashboard:stats_update', callback);
  }

  // Subscribe to dashboard:refresh_events (señal para recargar lista de eventos desde API)
  subscribeToDashboardRefreshEvents(callback: () => void): () => void {
    this.on('dashboard:refresh_events', callback);
    return () => this.off('dashboard:refresh_events', callback);
  }

  subscribeToEmergencyStatus(callback: (data: { active: boolean }) => void): () => void {
    this.on('emergency:status', callback);
    return () => this.off('emergency:status', callback);
  }

  // ── ControlNat Enrollador ────────────────────────────────────────────────

  // enroller:status + enroller:paired (misma callback — el hook decide)
  subscribeToEnrollerStatus(callback: (data: any) => void): () => void {
    this.on('enroller:status', callback);
    this.on('enroller:paired', callback);
    return () => {
      this.off('enroller:status', callback);
      this.off('enroller:paired', callback);
    };
  }

  // Progreso + resultado de UNA sesión de captura (filtra por session_id)
  subscribeToEnrollerCapture(sessionId: string, callback: (data: any) => void): () => void {
    const filtered = (data: any) => {
      if (data?.session_id === sessionId) callback(data);
    };
    this.on('enroller:capture_progress', filtered);
    this.on('enroller:capture_result', filtered);
    return () => {
      this.off('enroller:capture_progress', filtered);
      this.off('enroller:capture_result', filtered);
    };
  }
}

export const websocketService = new WebSocketService();
export default websocketService;
